import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

export default function ContactForm() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Your message was sent. We will be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try calling us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-12 lg:gap-20" data-testid="contact-section">
      <aside className="lg:col-span-2">
        <p className="label-eyebrow mb-3">Reach out</p>
        <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1a1a1a] mb-6 leading-tight">We are here to listen.</h2>
        <div className="space-y-6 text-sm text-neutral-700">
          <div>
            <p className="label-eyebrow mb-2">Interventions & Coaching</p>
            {settings?.primary_email && <p className="mb-1"><a className="hover:text-[#6B1F2A]" href={`mailto:${settings.primary_email}`}>{settings.primary_email}</a></p>}
            {settings?.primary_phone && <p className="mb-1">Phone: <a className="hover:text-[#6B1F2A]" href={`tel:${settings.primary_phone}`}>{settings.primary_phone}</a></p>}
            {settings?.secondary_phone && <p>Phone: <a className="hover:text-[#6B1F2A]" href={`tel:${settings.secondary_phone}`}>{settings.secondary_phone}</a></p>}
          </div>
          <div>
            <p className="label-eyebrow mb-2">5329 Evaluations</p>
            {settings?.evaluations_email && <p className="mb-1"><a className="hover:text-[#6B1F2A]" href={`mailto:${settings.evaluations_email}`}>{settings.evaluations_email}</a></p>}
            {settings?.evaluations_phone && <p className="mb-1">Phone/Text: {settings.evaluations_phone}</p>}
            {settings?.fax && <p>Fax: {settings.fax}</p>}
          </div>
        </div>
      </aside>

      <form onSubmit={submit} className="lg:col-span-3 bg-[#F7F7F7] p-8 sm:p-12 border border-neutral-200" data-testid="contact-form">
        {done ? (
          <div className="py-10 text-center" data-testid="contact-success">
            <h3 className="font-serif-display text-3xl text-[#6B1F2A] mb-3">Thank you</h3>
            <p className="text-neutral-700">Your message was sent. A member of our team will be in touch shortly.</p>
            <button type="button" onClick={() => setDone(false)} className="btn-outline-burgundy mt-8" data-testid="send-another">Send another message</button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="label-eyebrow mb-2 block text-neutral-600">Name</label>
                <Input value={form.name} onChange={onChange("name")} placeholder="Your name" required data-testid="contact-name" />
              </div>
              <div>
                <label className="label-eyebrow mb-2 block text-neutral-600">Email</label>
                <Input type="email" value={form.email} onChange={onChange("email")} placeholder="you@example.com" required data-testid="contact-email" />
              </div>
              <div>
                <label className="label-eyebrow mb-2 block text-neutral-600">Phone</label>
                <Input value={form.phone} onChange={onChange("phone")} placeholder="Optional" data-testid="contact-phone" />
              </div>
              <div>
                <label className="label-eyebrow mb-2 block text-neutral-600">Subject</label>
                <Input value={form.subject} onChange={onChange("subject")} placeholder="What is this about?" data-testid="contact-subject" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow mb-2 block text-neutral-600">Message</label>
              <Textarea value={form.message} onChange={onChange("message")} placeholder="Tell us how we can help..." rows={6} required data-testid="contact-message" />
            </div>
            <button type="submit" disabled={submitting} className="btn-primary-burgundy disabled:opacity-60" data-testid="contact-submit">
              {submitting ? "Sending..." : "Send message"}
              <span aria-hidden>→</span>
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
